import { WabunkaExperienceListResponse } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_experience';

import { useAsyncFetchWithUniqueKeyFromPublicWabunkaApi } from '@/composables/models/wabunka/useAsyncFetchWithUniqueKeyFromPublicWabunkaApi';
import { useAsyncDataRequestStatus } from '@/composables/utils/useAsyncDataRequestStatus';
import { buildWabunkaExperienceListViewModel } from '@/models/wabunka/wabunkaExperience';
import { useCurrency } from '@/wabunka-src/common/composables/general/useCurrency/useCurrency';

export interface WabunkaExperiencesQuery {
  experienceEventStartAtDates?: string;
  destinationTagWebPaths?: string;
  themeTagWebPaths?: string;
  preferenceTagWebPaths?: string;
  page?: string;
  slugs?: string;
  perPage: string;
  includes: string;
  sort?: string;
  // APIはドット区切りのネストしたパラメータ形式を期待するため、クォートで囲んだ文字列キーを使用
  // 例: ?experienceEventStartTimeRange.from=04:00&experienceEventStartTimeRange.to=10:59
  'experienceEventStartTimeRange.from'?: string;
  'experienceEventStartTimeRange.to'?: string;
  currency?: string;
}

/**
 * Wabunka体験一覧情報の検索に使用する Composable
 * Page でのみ使用可能
 */
export const useAsyncWabunkaExperiencesSearch = async (query: {
  experienceEventStartAtDates: Ref<string>;
  destinations: Ref<string>;
  themes: Ref<string>;
  preferences?: Ref<string>;
  slugs: Ref<string[]>;
  perPage?: Ref<number>;
  page?: Ref<number>;
  experienceEventStartTimeRangeFrom?: Ref<string>;
  experienceEventStartTimeRangeTo?: Ref<string>;
  /** API のソートキー。おすすめ順は backend の既定値のため undefined を渡す */
  sort?: Ref<string | undefined>;
}) => {
  const path = computed(() => `/experiences/search`);
  const { selectedCurrency } = useCurrency();

  const queryRef = computed<WabunkaExperiencesQuery>(() => {
    const queryObj: WabunkaExperiencesQuery = {
      page: query.page?.value ? String(query.page.value) : undefined,
      perPage: query.perPage?.value ? String(query.perPage.value) : '0', // 0 で全件取得
      includes: 'experienceImages',
      currency: selectedCurrency.value !== 'JPY' ? selectedCurrency.value : undefined,
    };

    if (query.experienceEventStartAtDates?.value) {
      queryObj.experienceEventStartAtDates = query.experienceEventStartAtDates?.value;
    }

    if (query.destinations?.value) {
      queryObj.destinationTagWebPaths = query.destinations?.value;
    }

    if (query.themes?.value) {
      queryObj.themeTagWebPaths = query.themes?.value;
    }

    if (query.preferences?.value) {
      queryObj.preferenceTagWebPaths = query.preferences?.value;
    }

    if (query.slugs?.value?.length > 0) {
      queryObj.slugs = query.slugs?.value.join(',');
    }

    if (query.experienceEventStartTimeRangeFrom?.value) {
      queryObj['experienceEventStartTimeRange.from'] = query.experienceEventStartTimeRangeFrom.value;
    }

    if (query.experienceEventStartTimeRangeTo?.value) {
      queryObj['experienceEventStartTimeRange.to'] = query.experienceEventStartTimeRangeTo.value;
    }

    if (query.sort?.value) {
      queryObj.sort = query.sort.value;
    }

    return queryObj;
  });

  const { data, refresh, status } = await useAsyncFetchWithUniqueKeyFromPublicWabunkaApi({
    path,
    query: queryRef,
    options: { server: true, immediate: true },
    converter: WabunkaExperienceListResponse.fromJSON,
  });

  const experiences = computed(() => buildWabunkaExperienceListViewModel(data.value?.items));
  const totalCount = computed<number>(() => data.value?.maxCount ?? 0);
  const meta = computed(() => data.value?.searchResultPageMeta);

  // 「エラーなしで data が null」の取得失敗と「検索結果0件」を呼び出し側で区別するためのフラグ（JCAT-5203）。
  // 取得失敗を0件として扱うと、一覧系ページの shouldIndex が false になり noindex が配信される。
  const hasNoResponse = computed(() => data.value == null);

  return { experiences, meta, totalCount, hasNoResponse, refresh, ...useAsyncDataRequestStatus(status) };
};
